import { Card } from "@/components/ui/card";
import { Building2, Users, FolderKanban, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

export default function StatsWidget() {
  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });
  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
  });
  const { data: investments = [] } = useQuery({
    queryKey: ["investments"],
    queryFn: () => base44.entities.Investment.list(),
  });

  const totalDealValue = customers.reduce((sum, c) => sum + (c.deal_value || 0), 0);
  const totalCapital = investments.reduce((sum, i) => sum + (i.investment_amount || 0), 0);
  const activeCustomers = customers.filter((c) => c.status === "active").length;
  const investedCount = investors.filter((i) => i.status === "invested").length;
  const activeProjects = projects.filter((p) => p.status === "active").length;

  const stats = [
    {
      label: "Kunder",
      value: customers.length,
      icon: Building2,
      chip: "bg-brand-limeSoft text-brand-limeDark",
      hint: `${activeCustomers} aktive`,
    },
    {
      label: "Investorer",
      value: investors.length,
      icon: Users,
      chip: "bg-brand-ink text-brand-lime",
      hint: `${investedCount} investert`,
    },
    {
      label: "Prosjekter",
      value: projects.length,
      icon: FolderKanban,
      chip: "bg-slate-100 text-slate-700",
      hint: `${activeProjects} pågår`,
    },
    {
      label: "Innhentet kapital",
      value: `${(totalCapital / 1000000).toFixed(1)}M kr`,
      icon: TrendingUp,
      chip: "bg-emerald-50 text-emerald-600",
      hint: `${(totalDealValue / 1000000).toFixed(1)}M pipeline`,
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      {stats.map((stat, i) => (
        <Card
          key={i}
          className="group p-5 border border-slate-200/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="flex items-start justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${stat.chip}`}>
              <stat.icon className="w-[18px] h-[18px]" />
            </div>
          </div>
          <p className="mt-4 text-3xl font-extrabold text-brand-ink tracking-tight">{stat.value}</p>
          <p className="mt-1 text-xs text-slate-400">{stat.hint}</p>
        </Card>
      ))}
    </div>
  );
}
